
import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Palette, Plus, Trash2, Layout, Image as ImageIcon, Minus, Maximize2, GripHorizontal, PaintBucket, MessageSquare, Facebook, MapPin, Mail, Phone, Home, BarChart, Upload, Layers, ShoppingBag, Database, FileDown, CreditCard, Package, Box, ShieldCheck, FileText, RefreshCw, SlidersHorizontal, List, X } from 'lucide-react';
import ImagePickerModal from './ImagePickerModal';
import * as ReactRouterDOM from 'react-router-dom';
import { SectionType } from '../types';
import OrderManager from './OrderManager';
import PaymentConfigModal from './PaymentConfigModal';
import AIConfigModal from './AIConfigModal';
import GlobalAttributesManager from './GlobalAttributesManager';

const { useLocation } = ReactRouterDOM as any;

const EditControls: React.FC = () => {
  const { 
    config, 
    setPrimaryColor, 
    setSecondaryColor, 
    addPage, 
    pages, 
    deletePage, 
    updateConfig,
    themes,
    currentThemeId,
    applyTheme,
    addTheme,
    deleteTheme,
    addSection,
    deleteSection,
    validateCSVImport,
    applyCSVImport,
    restoreDefaults
  } = useApp();

  const location = useLocation();
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [newPageType, setNewPageType] = useState<'standard' | 'technology' | 'downloads' | 'contact' | 'portfolio'>('standard');
  
  const [newThemeName, setNewThemeName] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isOrderManagerOpen, setIsOrderManagerOpen] = useState(false);
  const [isPaymentConfigOpen, setIsPaymentConfigOpen] = useState(false);
  const [isAIConfigOpen, setIsAIConfigOpen] = useState(false);
  const [isGlobalAttrManagerOpen, setIsGlobalAttrManagerOpen] = useState(false);
  
  // Favicon Modal State
  const [isFaviconModalOpen, setIsFaviconModalOpen] = useState(false);
  
  // Import Ref
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importValidation, setImportValidation] = useState<{ success: boolean, errors: string[], warnings: string[], parsedCount: number, data: any } | null>(null);
  
  // Draggable logic state
  const [position, setPosition] = useState({ x: 0, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Initialize position on mount (top-right)
  useEffect(() => {
    setPosition({ x: Math.max(20, window.innerWidth - 350), y: 100 });
  }, []);

  // Find active page based on current URL
  const activePage = pages.find(p => p.slug === location.pathname);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Start dragging
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault(); // Prevent text selection
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;

      // Optional: boundary checks could be added here
      setPosition({
        x: newX,
        y: newY
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleAddPage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPageTitle && newPageSlug) {
      addPage(newPageTitle, newPageSlug.startsWith('/') ? newPageSlug : `/${newPageSlug}`, newPageType);
      setNewPageTitle('');
      setNewPageSlug('');
      setNewPageType('standard');
    }
  };

  const handleAddTheme = (e: React.FormEvent) => {
    e.preventDefault();
    if (newThemeName) {
      addTheme(newThemeName);
      setNewThemeName('');
    }
  };

  const handleCSVImport = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          const reader = new FileReader();
          reader.onload = (evt) => {
              const content = evt.target?.result as string;
              if (validateCSVImport) {
                  const result = validateCSVImport(content);
                  if (result.errors && result.errors.length > 0) {
                      alert("Błędy importu:\n" + result.errors.join("\n"));
                  } else {
                      setImportValidation(result);
                  }
              }
          };
          reader.readAsText(file); // Default UTF-8
      }
      // Reset input
      if (importInputRef.current) importInputRef.current.value = '';
  };

  const downloadCurrentCatalogCSV = () => {
      // NEW HEADER STRUCTURE: Page; Section; Product Name; Price; Description; Image URL
      const csvHeader = "Strona;Kategoria (Sekcja);Nazwa Produktu;Cena;Opis;Link do zdjęcia\n";
      
      const rows: string[] = [];

      pages.forEach(page => {
          page.sections.forEach(section => {
              if (section.type === SectionType.PRODUCT_GRID && section.items) {
                  section.items.forEach(item => {
                      // Escape semicolons in text to avoid CSV breaking
                      const cleanPage = (page.title || '').replace(/;/g, ',');
                      const cleanSection = (section.title || 'Inne').replace(/;/g, ',');
                      const cleanName = (item.title || '').replace(/;/g, ',');
                      const cleanDesc = (item.description || '').replace(/;/g, ',').replace(/\n/g, ' ');
                      const cleanPrice = (item.price || '0').replace(/;/g, '');
                      
                      // LOGIC: If image starts with 'data:', skip it to keep file size small
                      let cleanUrl = (item.imageUrl || '');
                      if (cleanUrl.startsWith('data:')) {
                          cleanUrl = ''; // Leave empty for local files
                      } else {
                          cleanUrl = cleanUrl.replace(/;/g, '');
                      }

                      rows.push(`${cleanPage};${cleanSection};${cleanName};${cleanPrice};${cleanDesc};${cleanUrl}`);
                  });
              }
          });
      });

      if (rows.length === 0) {
          rows.push("Kolekcja;Zmiękczacze;Przykładowy Produkt;1000;Opis produktu;https://example.com/image.jpg");
      }

      const csvContent = csvHeader + rows.join("\n");
      // Add BOM for Excel compatibility with UTF-8
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `katalog_produktow_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
  };

  // Dynamic styles for positioning
  const style = {
    left: `${position.x}px`,
    top: `${position.y}px`,
    touchAction: 'none' as const
  };

  if (isMinimized) {
    return (
      <div 
        style={style}
        onMouseDown={handleMouseDown}
        className="fixed z-[100] bg-white shadow-xl rounded-lg border border-gray-200 p-2 cursor-move flex items-center gap-2 animate-fade-in select-none"
        title="Przesuń lub rozwiń panel"
      >
        <div className="text-gray-400">
          <GripHorizontal size={20} />
        </div>
        <span className="font-bold text-xs text-gray-700">Ustawienia</span>
        <button 
          onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}
          className="bg-blue-100 hover:bg-blue-200 text-blue-600 p-1.5 rounded ml-2 transition-colors"
          title="Rozwiń panel"
          onMouseDown={(e) => e.stopPropagation()} 
        >
          <Maximize2 size={16} />
        </button>
      </div>
    );
  }

  return (
    <>
      <div 
        style={style}
        className="fixed z-[100] bg-white shadow-2xl rounded-lg border border-gray-200 w-80 max-h-[80vh] flex flex-col animate-fade-in"
      >
        {/* Draggable Header */}
        <div 
          className={`p-3 bg-gray-100 border-b flex items-center justify-between rounded-t-lg select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2 text-gray-700 font-bold">
            <GripHorizontal size={18} className="text-gray-400" />
            <h3>Ustawienia Witryny</h3>
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
            className="text-gray-500 hover:text-gray-800 p-1 hover:bg-gray-200 rounded transition-colors"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Minus size={18} />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-4 space-y-6 overflow-y-auto custom-scrollbar">
          
          {/* ORDERS & PAYMENTS BUTTONS */}
          <div className="grid grid-cols-2 gap-2">
              <button 
                 onClick={() => setIsOrderManagerOpen(true)}
                 className="bg-blue-600 text-white p-2 rounded text-xs font-bold shadow-md hover:bg-blue-700 transition-all flex flex-col items-center justify-center gap-1"
              >
                 <ShoppingBag size={18}/> Zamówienia
              </button>
              
              <button 
                 onClick={() => setIsPaymentConfigOpen(true)}
                 className="bg-gray-800 text-white p-2 rounded text-xs font-bold shadow-md hover:bg-gray-900 transition-all flex flex-col items-center justify-center gap-1"
              >
                 <CreditCard size={18}/> PayU
              </button>

              <button 
                 onClick={() => setIsAIConfigOpen(true)}
                 className="col-span-2 bg-purple-600 text-white p-2 rounded text-xs font-bold shadow-md hover:bg-purple-700 transition-all flex flex-col items-center justify-center gap-1"
              >
                 <MessageSquare size={18}/> Konfiguracja AI (Gemini)
              </button>

              <button 
                 onClick={() => {
                     const token = sessionStorage.getItem('adminToken') || '';
                     window.open(`api/backup.php?token=${token}`, '_blank');
                 }}
                 className="col-span-2 bg-emerald-600 text-white p-2 rounded text-xs font-bold shadow-md hover:bg-emerald-700 transition-all flex flex-col items-center justify-center gap-1"
              >
                 <Database size={18}/> Kopia Zapasowa (Baza DB)
              </button>
              <button 
                 onClick={() => setIsGlobalAttrManagerOpen(true)}
                 className="col-span-2 bg-orange-600 text-white p-2 rounded text-xs font-bold shadow-md hover:bg-orange-700 transition-all flex flex-col items-center justify-center gap-1"
              >
                 <List size={18}/> Globalne Cechy Produktów
              </button>
          </div>

          {/* COMPANY DATA */}
          <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                 <Home size={14}/> Dane Firmy
              </h4>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-3">
                  <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Nazwa Firmy</label>
                      <input 
                          type="text" 
                          value={config.companyName || ''} 
                          onChange={(e) => updateConfig({ companyName: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                      />
                  </div>
                  <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Adres</label>
                      <input 
                          type="text" 
                          value={config.address || ''} 
                          onChange={(e) => updateConfig({ address: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                      />
                  </div>
                  <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Email</label>
                      <input 
                          type="text" 
                          value={config.contactEmail || ''} 
                          onChange={(e) => updateConfig({ contactEmail: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                      />
                  </div>
                  <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Telefon</label>
                      <input 
                          type="text" 
                          value={config.contactPhone || ''} 
                          onChange={(e) => updateConfig({ contactPhone: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                      />
                  </div>
              </div>
          </div>

          {/* THEMES & APPEARANCE */}
          <div>
              <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                 <Palette size={14}/> Wygląd i Skórki
              </h4>
              <div className="bg-emerald-50 border border-emerald-100 p-3 rounded space-y-4">
                  <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Kolor Główny (Primary)</label>
                      <div className="flex items-center gap-2">
                          <input 
                              type="color" 
                              value={config.primaryColor} 
                              onChange={(e) => setPrimaryColor(e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                          />
                          <span className="text-xs text-gray-600 uppercase">{config.primaryColor}</span>
                      </div>
                  </div>
                  <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Kolor Poboczny (Secondary)</label>
                      <div className="flex items-center gap-2">
                          <input 
                              type="color" 
                              value={config.secondaryColor} 
                              onChange={(e) => setSecondaryColor(e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                          />
                          <span className="text-xs text-gray-600 uppercase">{config.secondaryColor}</span>
                      </div>
                  </div>
                  
                  <div className="border-t border-emerald-200 pt-3">
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">Gotowe Skórki (Motywy)</label>
                      <select 
                          className="w-full border border-emerald-300 rounded px-2 py-1 text-xs bg-white text-gray-700"
                          value={currentThemeId}
                          onChange={(e) => applyTheme(e.target.value)}
                      >
                          {themes.map(theme => (
                              <option key={theme.id} value={theme.id}>{theme.name}</option>
                          ))}
                      </select>
                  </div>
                  
                  <div className="border-t border-emerald-200 pt-3">
                      <label className="flex items-center gap-2 text-[10px] font-bold text-gray-500 uppercase cursor-pointer">
                          <input 
                              type="checkbox" 
                              checked={config.showCelebrationBanner}
                              onChange={(e) => updateConfig({ showCelebrationBanner: e.target.checked })}
                              className="accent-emerald-600"
                          />
                          Pasek Ogłoszeniowy (Promocje / Święta)
                      </label>
                  </div>
              </div>
          </div>

          {/* Active Page Management */}
          {activePage && (
            <div>
               <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Layers size={14}/> Edycja: {activePage.title}
               </h4>
               <div className="bg-blue-50 border border-blue-100 rounded p-3 space-y-3">
                  <p className="text-[10px] text-gray-500 mb-2">Dodaj nowe sekcje do tej podstrony.</p>
                  
                  <button 
                     onClick={() => addSection(activePage.id, SectionType.PRODUCT_GRID)}
                     className="w-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-600 hover:text-white px-3 py-2 rounded text-xs font-bold transition-all shadow-sm flex items-center justify-between group"
                  >
                     <span className="flex items-center gap-2"><ShoppingBag size={14}/> Kategoria Produktów</span>
                     <Plus size={14} className="group-hover:scale-125 transition-transform"/>
                  </button>

                  <button 
                     onClick={() => addSection(activePage.id, SectionType.TEXT_IMAGE)}
                     className="w-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 px-3 py-2 rounded text-xs font-medium transition-all shadow-sm flex items-center justify-between"
                  >
                     <span className="flex items-center gap-2"><ImageIcon size={14}/> Tekst + Zdjęcie</span>
                     <Plus size={14} />
                  </button>

                  <button 
                     onClick={() => addSection(activePage.id, SectionType.RICH_TEXT)}
                     className="w-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 px-3 py-2 rounded text-xs font-medium transition-all shadow-sm flex items-center justify-between"
                  >
                     <span className="flex items-center gap-2"><FileText size={14}/> Blok Tekstowy</span>
                     <Plus size={14} />
                  </button>

                  {/* Section List / Delete */}
                  <div className="mt-4 border-t border-blue-100 pt-3">
                     <p className="text-[10px] font-bold text-gray-500 mb-2 uppercase">Sekcje na stronie:</p>
                     <ul className="space-y-1">
                        {activePage.sections.map((s, idx) => (
                           <li key={s.id} className="flex justify-between items-center text-xs bg-white px-2 py-1 rounded border border-gray-100">
                              <span className="truncate max-w-[150px]">{idx + 1}. {s.title || s.type}</span>
                              <button 
                                 onClick={() => {
                                    if(confirm('Usunąć tę sekcję?')) deleteSection(activePage.id, s.id)
                                 }}
                                 className="text-gray-400 hover:text-red-500 p-1"
                              >
                                 <Trash2 size={12}/>
                              </button>
                           </li>
                        ))}
                     </ul>
                  </div>
               </div>
            </div>
          )}
          
          <hr />
          
          {/* BULK IMPORT */}
          <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                 <Database size={14}/> Baza Produktów (CSV)
              </h4>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-3">
                  <p className="text-xs text-gray-500 leading-relaxed">
                      Zarządzaj ofertą masowo. Pobierz obecną ofertę, edytuj w Excelu i wgraj ponownie. <br/>
                      Format: Strona; Kategoria; Produkt...
                  </p>
                  
                  <button 
                    onClick={downloadCurrentCatalogCSV}
                    className="w-full bg-white border border-gray-300 text-gray-600 hover:text-black hover:bg-gray-50 py-2 rounded text-xs font-medium transition-all flex items-center justify-center gap-2"
                  >
                     <FileDown size={14}/> Pobierz Obecny Katalog
                  </button>

                  <button 
                    onClick={() => importInputRef.current?.click()}
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 py-2 rounded text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                     <Upload size={14}/> Wgraj plik CSV
                  </button>

                  <input 
                    type="file" 
                    accept=".csv,.txt"
                    ref={importInputRef}
                    className="hidden"
                    onChange={handleCSVImport}
                  />
                  
                  {/* RESTORE BUTTON */}
                  <div className="pt-2 mt-2 border-t border-gray-200">
                     <button onClick={restoreDefaults} className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-1">
                         <RefreshCw size={10} /> Przywróć strony prawne (Regulamin, RODO)
                     </button>
                  </div>
              </div>
          </div>
          
        </div>
      </div>
      
      {/* Favicon Picker Modal */}
      <ImagePickerModal 
          isOpen={isFaviconModalOpen}
          onClose={() => setIsFaviconModalOpen(false)}
          onSave={(url) => {
             const finalUrl = Array.isArray(url) ? url[0] : url;
             updateConfig({ faviconUrl: finalUrl });
          }}
          initialUrl={config.faviconUrl}
          title="Wybierz Ikonę Strony (Favicon)"
      />

      <OrderManager 
          isOpen={isOrderManagerOpen}
          onClose={() => setIsOrderManagerOpen(false)}
      />
      
      <PaymentConfigModal 
          isOpen={isPaymentConfigOpen}
          onClose={() => setIsPaymentConfigOpen(false)}
      />

      <AIConfigModal 
          isOpen={isAIConfigOpen}
          onClose={() => setIsAIConfigOpen(false)}
      />

      <GlobalAttributesManager 
          isOpen={isGlobalAttrManagerOpen}
          onClose={() => setIsGlobalAttrManagerOpen(false)}
      />

      {importValidation && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                  <div className="p-4 border-b bg-gray-50 flex justify-between items-center shrink-0">
                      <h3 className="font-bold text-gray-800 flex items-center gap-2">
                          <Upload size={20} /> Weryfikacja Importu
                      </h3>
                      <button onClick={() => setImportValidation(null)} className="p-2 hover:bg-gray-200 rounded"><X size={20}/></button>
                  </div>
                  
                  <div className="p-6 space-y-6 overflow-y-auto">
                      <div className="bg-blue-50 p-4 border border-blue-100 rounded text-sm text-blue-800">
                          <strong>Pomyślnie przetworzono plik!</strong><br />
                          Znaleziono {importValidation.parsedCount} produktów.
                      </div>

                      {importValidation.warnings && importValidation.warnings.length > 0 && (
                          <div className="bg-orange-50 p-4 border border-orange-200 rounded space-y-2">
                              <h4 className="text-xs font-bold text-orange-800 uppercase flex items-center gap-1">
                                  Ostrzeżenia ({importValidation.warnings.length})
                              </h4>
                              <ul className="list-disc list-inside text-xs text-orange-700 space-y-1">
                                  {importValidation.warnings.map((w, i) => (
                                      <li key={i}>{w}</li>
                                  ))}
                              </ul>
                          </div>
                      )}

                      <div className="space-y-3">
                          <p className="text-sm text-gray-600 font-medium">Jak chcesz zaimportować dane?</p>
                          <div className="grid grid-cols-2 gap-4">
                              <button 
                                  onClick={() => {
                                      if (applyCSVImport) {
                                          applyCSVImport(importValidation.data, 'replace');
                                          setImportValidation(null);
                                          alert("Zastąpiono bazę produktów nowymi danymi.");
                                      }
                                  }}
                                  className="border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 p-4 rounded text-center transition-colors shadow-sm"
                              >
                                  <div className="font-bold mb-1">Zastąp Bazę</div>
                                  <div className="text-[10px] opacity-80">Obecne produkty zostaną usunięte, a na ich miejsce wejdą nowe, nadpisując strukturę. Formularze prawne zostaną zachowane.</div>
                              </button>

                              <button 
                                  onClick={() => {
                                      if (applyCSVImport) {
                                          applyCSVImport(importValidation.data, 'append');
                                          setImportValidation(null);
                                          alert("Dodano nowe dane do bazy.");
                                      }
                                  }}
                                  className="border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 p-4 rounded text-center transition-colors shadow-sm"
                              >
                                  <div className="font-bold mb-1">Przemnożenie (Dopisz)</div>
                                  <div className="text-[10px] opacity-80">Istniejące produkty zostaną zachowane, nowe wpisy z pliku CSV zostaną dopisane jako dodatkowe warianty do kategorii.</div>
                              </button>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </>
  );
};

export default EditControls;
